import { useState } from 'react';
import type { PeriodoFiltro } from '../solicitacoesDashboard.utils';

interface PeriodoSelectorProps {
  periodo: PeriodoFiltro;
  onChange: (periodo: PeriodoFiltro) => void;
}

type PresetPeriodo = 'tudo' | 'hoje' | 'mes_atual' | 'mes_anterior' | 'personalizado';

function inicioDoDia(data: Date): Date {
  const resultado = new Date(data);
  resultado.setHours(0, 0, 0, 0);
  return resultado;
}

function fimDoDia(data: Date): Date {
  const resultado = new Date(data);
  resultado.setHours(23, 59, 59, 999);
  return resultado;
}

const PRESETS: Record<Exclude<PresetPeriodo, 'personalizado'>, { label: string; calcular: () => PeriodoFiltro }> = {
  tudo: { label: 'Tudo', calcular: () => ({ de: null, ate: null }) },
  hoje: {
    label: 'Hoje',
    calcular: () => ({ de: inicioDoDia(new Date()), ate: fimDoDia(new Date()) }),
  },
  mes_atual: {
    label: 'Este mês',
    calcular: () => {
      const hoje = new Date();
      return { de: new Date(hoje.getFullYear(), hoje.getMonth(), 1), ate: fimDoDia(hoje) };
    },
  },
  mes_anterior: {
    label: 'Mês passado',
    calcular: () => {
      const hoje = new Date();
      const ultimoDiaMesAnterior = new Date(hoje.getFullYear(), hoje.getMonth(), 0);
      return {
        de: new Date(ultimoDiaMesAnterior.getFullYear(), ultimoDiaMesAnterior.getMonth(), 1),
        ate: fimDoDia(ultimoDiaMesAnterior),
      };
    },
  },
};

function paraInputDate(data: Date | null): string {
  if (!data) return '';
  const ano = data.getFullYear();
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${ano}-${mes}-${dia}`;
}

function deInputDate(valor: string): Date | null {
  if (!valor) return null;
  const [ano, mes, dia] = valor.split('-').map(Number);
  return new Date(ano, mes - 1, dia);
}

/**
 * Filtro de período do Dashboard — atalhos comuns (Hoje/Este mês/Mês passado/Tudo) mais
 * um intervalo livre (De/Até), pra ver as métricas de um mês específico ou de qualquer
 * recorte de tempo, em vez de sempre a fila inteira desde o início. Filtra pela
 * `dataAbertura` da Solicitação (ver `filtrarPorPeriodo`).
 */
export default function PeriodoSelector({ periodo, onChange }: PeriodoSelectorProps) {
  const [presetAtivo, setPresetAtivo] = useState<PresetPeriodo>('tudo');

  const handlePreset = (chave: Exclude<PresetPeriodo, 'personalizado'>) => {
    setPresetAtivo(chave);
    onChange(PRESETS[chave].calcular());
  };

  const handleDataPersonalizada = (campo: 'de' | 'ate', valor: string) => {
    setPresetAtivo('personalizado');
    const data = deInputDate(valor);
    onChange({ ...periodo, [campo]: campo === 'ate' && data ? fimDoDia(data) : data });
  };

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      {(Object.keys(PRESETS) as Array<Exclude<PresetPeriodo, 'personalizado'>>).map((chave) => (
        <button
          key={chave}
          type="button"
          onClick={() => handlePreset(chave)}
          className={`rounded-full px-3 py-1 font-medium ${
            presetAtivo === chave ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {PRESETS[chave].label}
        </button>
      ))}
      <div className="ml-2 flex items-center gap-1.5 text-gray-500">
        <input
          type="date"
          className="input-primary py-1 text-xs"
          value={paraInputDate(periodo.de)}
          onChange={(event) => handleDataPersonalizada('de', event.target.value)}
        />
        <span>até</span>
        <input
          type="date"
          className="input-primary py-1 text-xs"
          value={paraInputDate(periodo.ate)}
          onChange={(event) => handleDataPersonalizada('ate', event.target.value)}
        />
      </div>
    </div>
  );
}
